import {
  Button,
  Card,
  Link as ExternalLink,
  Heading,
} from "@digdir/designsystemet-react";
import { DownloadIcon } from "@navikt/aksel-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type {
  MålerdataOpplastingerStatus,
  OppgjørForPeriode,
  ValidationProblemDetails,
} from "@/api/generated";
import {
  getOppgjørForPeriodeQueryKey,
  postInnrapporteringUploadMutation,
} from "@/api/generated/@tanstack/react-query.gen";
import { UploadFileButton } from "@/components/UploadFileButton";
import { ValidationProblems } from "@/components/ValidationProblems";
import { useToaster } from "@/features/toaster";
import { cn } from "@/utils";
import { formatDate } from "@/utils/dateTimeUtils";

export const MålerdataGrunnlag = ({
  oppgjør,
  organisasjonsNummer,
  year,
  month,
  className,
  opplastingStatus,
}: {
  organisasjonsNummer: number;
  oppgjør?: OppgjørForPeriode | null;
  year: number;
  month: number;
  className?: string;
  opplastingStatus?: MålerdataOpplastingerStatus | null;
}) => {
  const { t } = useTranslation();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const innrapporteringMutation = useMutation({
    ...postInnrapporteringUploadMutation({
      client: httpClient,
    }),
  });

  const hasOppgjør = oppgjør != null;

  // Ikke opplastet eller opplastet men ikke låst
  const uploadFileEnabled = !hasOppgjør || opplastingStatus === "Opplastet";

  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [fileUploadError, setFileUploadError] =
    useState<ValidationProblemDetails | null>(null);

  const downloadUrl =
    httpClient.getConfig().baseURL +
    `/selskapsportal/maalerdata/${organisasjonsNummer}/periode/grunnlag?year=${year}&month=${month}`;

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      throw new Error("No file selected");
    }

    setFileUploadError(null);
    setFileUploadLoading(true);

    innrapporteringMutation.mutate(
      {
        body: { file: file },
        path: { organisasjonsNummer: organisasjonsNummer },
        query: { year, month },
        headers: { "Content-Type": "multipart/form-data" },
      },
      {
        onError: (error) => {
          console.error(
            `Could not upload målerdata grunnlag for periode ${year}-${month} for org: ${organisasjonsNummer}`,
            error,
          );
          const problemDetails = error.response
            ?.data as ValidationProblemDetails;
          setFileUploadError(problemDetails);
        },
        onSuccess: () => {
          toaster.success({
            messageKey: "pages.maaledata-periode.grunnlag.lastet-opp-suksess",
          });

          queryClient.invalidateQueries({
            queryKey: getOppgjørForPeriodeQueryKey({
              path: {
                organisasjonsNummer: organisasjonsNummer,
              },
              query: { month, year },
              // NB: Important to include client to generate correct query key!
              // Otherwise the base path might not be the same
              client: httpClient,
            }),
          });
        },
        onSettled: () => {
          setFileUploadLoading(false);
        },
      },
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      <Heading level={3}>
        {t("pages.maaledata-periode.grunnlag.tittel")}
      </Heading>
      {fileUploadLoading !== true && fileUploadError != null && (
        <ValidationProblems
          validationProblemDetails={fileUploadError}
          maxErrorsToShow={5}
          message={t(
            "pages.maaledata-periode.grunnlag.valideringsFeil.melding",
          )}
        />
      )}
      <Card
        variant="tinted"
        className={`border-dashed ${fileUploadLoading ? "animate-pulse motion-reduce:animate-none" : ""}`}
      >
        {fileUploadLoading === true ? (
          <span>{t("pages.maaledata-periode.grunnlag.laster-opp-fil")}</span>
        ) : hasOppgjør ? (
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col">
              <span>{oppgjør.grunnlag.filnavn}</span>
              <span className="text-balder-text-subtle">
                {t("pages.maaledata-periode.grunnlag.lastet-opp")}:{" "}
                {formatDate(
                  new Date(oppgjør.grunnlag.lastetOpp),
                  "short",
                  true,
                )}
              </span>
            </div>
            <div className="flex gap-4 justify-self-end">
              <Button variant="secondary" asChild>
                <ExternalLink
                  href={downloadUrl}
                  download={oppgjør.grunnlag.filnavn}
                  className="w-fit whitespace-nowrap flex flex-row gap-2 items-center"
                >
                  <DownloadIcon />
                  {t("pages.maaledata-periode.grunnlag.last-ned")}
                </ExternalLink>
              </Button>
              {uploadFileEnabled && (
                <UploadFileButton
                  text={t("pages.maaledata-periode.grunnlag.erstatt-fil")}
                  acceptFileType=".csv"
                  onFileChanged={handleUpload}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-row justify-between items-center gap-4">
            <span>{t("pages.maaledata-periode.grunnlag.ingen-fil-valgt")}</span>
            {uploadFileEnabled && (
              <UploadFileButton
                text={t("pages.maaledata-periode.grunnlag.last-opp")}
                acceptFileType=".csv"
                onFileChanged={handleUpload}
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
